/**
 * 结构化成果（Artifact）store（U1 架构 §2 / T08 / T11 / T15）
 *  - byConv：conversationId -> Artifact[]（实时 artifact:upsert / artifact:confirm 驱动）
 *  - pendingCount：conversationId -> 待确认(decision/action)数量
 *  - aiMode：conversationId -> quiet|partner|host（ai:mode 驱动）
 *  - notices：conversationId -> 最近 ai:notice（NoticeStrip 渲染）
 *  - 创建/编辑/预览对话框的开关状态（由消息「+」菜单或面板按钮触发）
 *
 * 由 socket/client.ts 在收到 U1 实时事件时调用这里的 action。
 */
import { create } from 'zustand';
import type { AiMode, AiNotice, Artifact, ArtifactType } from '../types/models';

interface ArtifactState {
  byConv: Record<number, Artifact[]>;
  pendingCount: Record<number, number>;
  aiMode: Record<number, AiMode>;
  notices: Record<number, AiNotice[]>;

  /** 创建对话框 */
  createOpen: boolean;
  createConvId: number | null;
  createType: ArtifactType;
  createSourceMessageId: number | null;
  /** 编辑对话框 */
  editId: number | null;
  /** 预览对话框 */
  previewId: number | null;
  /** 成果面板是否展开（窄屏可收起） */
  panelOpen: boolean;

  setArtifacts: (conversationId: number, list: Artifact[]) => void;
  upsertArtifact: (conversationId: number, a: Artifact) => void;
  /** 部分更新（artifact:confirm 仅携带 {id,state,confirmed_by,confirmed_at}） */
  patchArtifact: (
    conversationId: number,
    id: number,
    patch: Partial<Artifact>,
  ) => void;
  setPendingCount: (conversationId: number, n: number) => void;
  recomputePending: (conversationId: number) => void;
  setAiMode: (conversationId: number, mode: AiMode) => void;
  addNotice: (conversationId: number, n: AiNotice) => void;
  /** 清除某会话的提示（用户点击「知道了」） */
  clearNotices: (conversationId: number) => void;

  openCreate: (conversationId: number, sourceMessageId?: number | null, type?: ArtifactType) => void;
  closeCreate: () => void;
  openEdit: (artifactId: number) => void;
  closeEdit: () => void;
  openPreview: (artifactId: number) => void;
  closePreview: () => void;
  togglePanel: (open?: boolean) => void;

  reset: () => void;
}

export const useArtifactStore = create<ArtifactState>((set) => ({
  byConv: {},
  pendingCount: {},
  aiMode: {},
  notices: {},
  createOpen: false,
  createConvId: null,
  createType: 'issue',
  createSourceMessageId: null,
  editId: null,
  previewId: null,
  panelOpen: true,

  setArtifacts: (conversationId, list) =>
    set((s) => ({
      byConv: { ...s.byConv, [conversationId]: list },
      pendingCount: {
        ...s.pendingCount,
        [conversationId]: list.filter((a) => a.state === 'pending').length,
      },
    })),

  upsertArtifact: (conversationId, a) =>
    set((s) => {
      const list = s.byConv[conversationId] ? [...s.byConv[conversationId]] : [];
      const idx = list.findIndex((x) => x.id === a.id);
      if (idx >= 0) list[idx] = a;
      else list.push(a);
      list.sort((x, y) => x.type.localeCompare(y.type) || x.id - y.id);
      return {
        byConv: { ...s.byConv, [conversationId]: list },
        pendingCount: {
          ...s.pendingCount,
          [conversationId]: list.filter((x) => x.state === 'pending').length,
        },
      };
    }),

  setPendingCount: (conversationId, n) =>
    set((s) => ({ pendingCount: { ...s.pendingCount, [conversationId]: n } })),

  recomputePending: (conversationId) =>
    set((s) => {
      const list = s.byConv[conversationId] ?? [];
      return {
        pendingCount: {
          ...s.pendingCount,
          [conversationId]: list.filter((x) => x.state === 'pending').length,
        },
      };
    }),

  setAiMode: (conversationId, mode) =>
    set((s) => ({ aiMode: { ...s.aiMode, [conversationId]: mode } })),

  patchArtifact: (conversationId, id, patch) =>
    set((s) => {
      const list = s.byConv[conversationId] ? [...s.byConv[conversationId]] : [];
      const idx = list.findIndex((x) => x.id === id);
      if (idx < 0) return {};
      list[idx] = { ...list[idx], ...patch };
      return {
        byConv: { ...s.byConv, [conversationId]: list },
        pendingCount: {
          ...s.pendingCount,
          [conversationId]: list.filter((x) => x.state === 'pending').length,
        },
      };
    }),

  addNotice: (conversationId, n) =>
    set((s) => {
      const list = s.notices[conversationId] ? [...s.notices[conversationId]] : [];
      list.unshift(n);
      // 仅保留最近 5 条
      return { notices: { ...s.notices, [conversationId]: list.slice(0, 5) } };
    }),

  clearNotices: (conversationId) =>
    set((s) => ({ notices: { ...s.notices, [conversationId]: [] } })),

  openCreate: (conversationId, sourceMessageId = null, type = 'issue') =>
    set({ createOpen: true, createConvId: conversationId, createType: type, createSourceMessageId: sourceMessageId }),
  closeCreate: () => set({ createOpen: false, createConvId: null, createSourceMessageId: null }),
  openEdit: (artifactId) => set({ editId: artifactId }),
  closeEdit: () => set({ editId: null }),
  openPreview: (artifactId) => set({ previewId: artifactId }),
  closePreview: () => set({ previewId: null }),
  togglePanel: (open) => set((s) => ({ panelOpen: open ?? !s.panelOpen })),

  reset: () =>
    set({
      byConv: {},
      pendingCount: {},
      aiMode: {},
      notices: {},
      createOpen: false,
      createConvId: null,
      createType: 'issue',
      createSourceMessageId: null,
      editId: null,
      previewId: null,
      panelOpen: true,
    }),
}));

/** 当前会话的 AI 模式（优先实时 aiMode，回退 conversation.ai_mode，再回退 quiet） */
export function currentAiMode(
  conversationId: number,
  fallback?: string | null,
): AiMode {
  const rt = useArtifactStore.getState().aiMode[conversationId];
  if (rt) return rt;
  if (fallback === 'quiet' || fallback === 'partner' || fallback === 'host') return fallback;
  return 'quiet';
}
